import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getUserDetail,
  adjustCredits,
  unlockUser,
  verifyUserEmail,
  blockUser,
  unblockUser,
  AdminUserDetail as AdminUserDetailType,
  generationStatusPillClass,
  formatMicroUsd,
} from '../../lib/adminApi';
import { formatDate } from '../../lib/date';
import GenerationPreviewModal from './GenerationPreviewModal';

function AdjustCreditsModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (delta: string, note: string) => Promise<void>;
}) {
  const [delta, setDelta] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!delta || !note) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(delta.trim(), note.trim());
      onClose();
    } catch {
      setError('Failed to adjust credits.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <h2>Adjust Credits</h2>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="admin-filter-group" style={{ marginBottom: '1rem' }}>
            <label className="admin-filter-label">Delta (e.g. +10 or -5)</label>
            <input
              className="admin-filter-input"
              value={delta}
              onChange={e => setDelta(e.target.value)}
              placeholder="+10 or -5"
              style={{ width: '100%' }}
              required
            />
          </div>
          <div className="admin-filter-group" style={{ marginBottom: '1rem' }}>
            <label className="admin-filter-label">Note (required)</label>
            <input
              className="admin-filter-input"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Reason for adjustment"
              style={{ width: '100%' }}
              required
            />
          </div>
          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !delta || !note}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BlockModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(reason);
      onClose();
    } catch {
      setError('Failed to block user.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <h2>Block User</h2>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="admin-filter-group" style={{ marginBottom: '1rem' }}>
            <label className="admin-filter-label">Reason (required)</label>
            <input
              className="admin-filter-input"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason for blocking"
              style={{ width: '100%' }}
              required
            />
          </div>
          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              style={{ background: 'var(--error)' }}
              disabled={submitting || !reason}
            >
              {submitting ? 'Blocking…' : 'Block User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const REASON_LABELS: Record<string, string> = {
  REGISTRATION_BONUS: 'Registration Bonus',
  GENERATION: 'AI Generation',
  REFUND: 'Refund',
  TOPUP: 'Top-up',
  ADMIN_ADJUSTMENT: 'Admin Adjustment',
  EXPIRY: 'Credit Expiry',
};

type Tab = 'credits' | 'generations';

function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [userData, setUserData] = useState<AdminUserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tab, setTab] = useState<Tab>('credits');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [actionError, setActionError] = useState('');
  const [previewGenerationId, setPreviewGenerationId] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const result = await getUserDetail(id);
      setUserData(result);
    } catch {
      setError('Failed to load user.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleAdjustCredits = async (delta: string, note: string) => {
    if (!id) return;
    await adjustCredits(id, delta, note);
    await fetchUser();
  };

  const handleUnlock = async () => {
    if (!id) return;
    if (!window.confirm('Unlock this account?')) return;
    setActionError('');
    try {
      await unlockUser(id);
      await fetchUser();
    } catch {
      setActionError('Failed to unlock user.');
    }
  };

  const handleVerifyEmail = async () => {
    if (!id) return;
    if (!window.confirm('Force-verify this email?')) return;
    setActionError('');
    try {
      await verifyUserEmail(id);
      await fetchUser();
    } catch {
      setActionError('Failed to verify email.');
    }
  };

  const handleBlock = async (reason: string) => {
    if (!id) return;
    await blockUser(id, reason);
    await fetchUser();
  };

  const handleUnblock = async () => {
    if (!id) return;
    if (!window.confirm('Unblock this user?')) return;
    setActionError('');
    try {
      await unblockUser(id);
      await fetchUser();
    } catch {
      setActionError('Failed to unblock user.');
    }
  };

  const isLocked = userData?.lockedUntil
    ? new Date(userData.lockedUntil) > new Date()
    : false;

  return (
    <div>
      <div className="admin-topbar" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button className="btn-ghost" onClick={() => navigate('/admin/users')}>← Back</button>
        <h1>User Detail</h1>
      </div>
      <div className="admin-content">
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner" />
          </div>
        )}

        {error && (
          <div className="alert alert-error" role="alert">{error}</div>
        )}

        {!loading && !error && userData && (
          <>
            {/* Profile header */}
            <div className="admin-user-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary-dark)' }}>
                      {userData.email}
                    </h2>
                    {userData.role === 'admin' && (
                      <span className="admin-pill admin-pill-admin">Admin</span>
                    )}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                    {userData.businessName}
                    {userData.mobile && ` · ${userData.mobile}`}
                  </div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '0.75rem' }}>
                    Joined {formatDate(userData.createdAt)}
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-dark)' }}>
                    {userData.balance}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    credits
                  </div>
                </div>
              </div>

              {/* Status row */}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                {userData.emailVerified ? (
                  <span className="admin-pill admin-pill-success">Email Verified</span>
                ) : (
                  <span className="admin-pill admin-pill-pending">Email Unverified</span>
                )}
                {isLocked && (
                  <span className="admin-pill admin-pill-pending">
                    Locked until {formatDate(userData.lockedUntil!)}
                  </span>
                )}
                {userData.blockedAt && (
                  <span className="admin-pill admin-pill-blocked">
                    Blocked {formatDate(userData.blockedAt)}
                  </span>
                )}
              </div>
            </div>

            {/* Action buttons */}
            {actionError && (
              <div className="alert alert-error" role="alert" style={{ marginBottom: '1rem' }}>{actionError}</div>
            )}
            <div className="admin-user-actions">
              <button className="btn-secondary" onClick={() => setShowAdjustModal(true)}>
                Adjust Credits
              </button>
              <button
                className="btn-secondary"
                onClick={handleUnlock}
                disabled={!isLocked}
              >
                Unlock Account
              </button>
              <button
                className="btn-secondary"
                onClick={handleVerifyEmail}
                disabled={userData.emailVerified}
              >
                Force Verify Email
              </button>
              {userData.blockedAt ? (
                <button className="btn-secondary" onClick={handleUnblock}>
                  Unblock
                </button>
              ) : (
                <button
                  className="btn-secondary"
                  style={{ color: 'var(--error)', borderColor: 'var(--error)' }}
                  onClick={() => setShowBlockModal(true)}
                >
                  Block User
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="admin-tabs">
              <div
                className={`admin-tab${tab === 'credits' ? ' active' : ''}`}
                onClick={() => setTab('credits')}
              >
                Credit History
              </div>
              <div
                className={`admin-tab${tab === 'generations' ? ' active' : ''}`}
                onClick={() => setTab('generations')}
              >
                Generations
              </div>
            </div>

            {tab === 'credits' && (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Reason</th>
                      <th>Delta</th>
                      <th>Balance After</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userData.creditTransactions.length === 0 ? (
                      <tr><td colSpan={5} className="admin-table-empty">No transactions.</td></tr>
                    ) : (
                      userData.creditTransactions.map(tx => {
                        const positive = !tx.delta.startsWith('-');
                        return (
                          <tr key={tx.id}>
                            <td>{formatDate(tx.createdAt)}</td>
                            <td>{REASON_LABELS[tx.reason] ?? tx.reason}</td>
                            <td style={{ color: positive ? 'var(--success)' : 'var(--error)', fontWeight: 600 }}>
                              {positive ? `+${tx.delta}` : tx.delta}
                            </td>
                            <td>{tx.balanceAfter}</td>
                            <td style={{ color: 'var(--text-faint)', fontSize: '0.8rem' }}>
                              {tx.note ?? '-'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'generations' && (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Provider</th>
                      <th>Cost (micro-USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userData.generations.length === 0 ? (
                      <tr><td colSpan={4} className="admin-table-empty">No generations.</td></tr>
                    ) : (
                      userData.generations.map(g => (
                        <tr key={g.id} onClick={() => setPreviewGenerationId(g.id)}>
                          <td>{formatDate(g.createdAt)}</td>
                          <td>
                            <span className={`admin-pill admin-pill-${generationStatusPillClass(g.status)}`}>
                              {g.status}
                            </span>
                          </td>
                          <td>{g.aiProvider}</td>
                          <td>{g.costMicroUsd}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {showAdjustModal && (
        <AdjustCreditsModal
          onClose={() => setShowAdjustModal(false)}
          onSubmit={handleAdjustCredits}
        />
      )}

      {showBlockModal && (
        <BlockModal
          onClose={() => setShowBlockModal(false)}
          onSubmit={handleBlock}
        />
      )}

      {previewGenerationId && (
        <GenerationPreviewModal
          generationId={previewGenerationId}
          onClose={() => setPreviewGenerationId(null)}
        />
      )}
    </div>
  );
}

export default AdminUserDetail;
